// #1890 — the QR onboarding gates, as rules rather than as markup.
//
// #1827 shipped four gates and enforces them on every public request. The admin
// UI exposed none of them, so a local could not turn a QR on, cap it, expire it
// or require SMS — the only way to change one was a hand-written UPDATE on
// prod-29.
//
//   node callsteward-web/tests/qr-gate-rules-1890.test.mjs
//
// These live here because each has a wrong version that looks perfectly
// reasonable in a template: a blank expiry that means "never", a cap of zero
// that reads as "unlimited", and an SMS switch whose off position is the one
// that matters.

/** Days added to today when QR is switched on and no expiry has been set. */
export const DEFAULT_ACTIVE_DAYS = 90;

/** `YYYY-MM-DD`, the shape the date input and the API both want. */
export function defaultActiveUntil(now) {
    const base = now instanceof Date ? new Date(now.getTime()) : new Date();
    base.setDate(base.getDate() + DEFAULT_ACTIVE_DAYS);

    return base.toISOString().slice(0, 10);
}

/**
 * What the admin needs told before this is saved.
 *
 * ⚠ THE ZERO TRAP IS THE IMPORTANT ONE. `qr_daily_cap = 0` blocks EVERY
 * submission — it is fail-closed, not unlimited — and "0" is exactly what
 * someone types when they mean "no limit". The gate refuses silently from the
 * worker's side, so nobody finds out until a rep is standing next to a poster
 * that does not work.
 *
 * @returns {{key: string, text: string, blocking: boolean}[]}
 */
export function qrWarnings(form) {
    const out = [];
    if (!form || !form.qr_enabled) return out;

    if (Number(form.qr_daily_cap) === 0) {
        out.push({
            key: 'zero-cap',
            text: 'A daily cap of 0 blocks every submission — it does not mean unlimited. Nobody will be able to use this code.',
            blocking: true,
        });
    }

    if (!form.qr_active_until) {
        out.push({
            key: 'no-expiry',
            text: 'With no expiry this code works forever. A QR printed on a poster outlives the event it was made for.',
            blocking: true,
        });
    }

    if (!form.qr_require_sms_verify) {
        out.push({
            key: 'no-sms',
            text: 'Without SMS verification anyone who can see the code can start an intake. This is the only check that costs an automated scan anything.',
            blocking: true,
        });
    }

    return out;
}

/**
 * Warnings that must be acknowledged before saving.
 *
 * ⚠ Only when the QR is ON. The same settings on a disabled profile are stored
 * values nobody can reach, and interrupting someone about a code that does not
 * work is how a confirmation becomes something you click through.
 */
export function blockingWarnings(form) {
    return qrWarnings(form).filter((w) => w.blocking);
}

/**
 * The `qr_*` half of the save payload.
 *
 * ⚠ `qr_daily_cap` is ALWAYS a number here. The column is nullable and NULL
 * means uncapped, but `ProfileController::update` validates it as
 * `sometimes|integer|min:0|max:1000` with no `nullable` — so null cannot be sent,
 * and "uncapped" is not reachable from this screen at all. For a code printed in
 * public that is the right way round: uncapped is the setting you would least
 * want a one-click path to. Recorded rather than worked around.
 */
export function qrPayload(form) {
    return {
        qr_enabled: !!form.qr_enabled,
        qr_active_until: form.qr_active_until || null,
        qr_daily_cap: clampCap(form.qr_daily_cap),
        qr_require_sms_verify: !!form.qr_require_sms_verify,
    };
}

/** 0–1000, the server's own bounds, so a typo is corrected here and not rejected there. */
export function clampCap(value) {
    const n = Math.round(Number(value));
    if (!isFinite(n)) return 20;

    return Math.min(1000, Math.max(0, n));
}

/** The `qr_*` half of the form, from a profile row. */
export function qrFormFrom(row) {
    return {
        qr_enabled: !!(row && row.qr_enabled),
        qr_active_until: (row && row.qr_active_until) ? String(row.qr_active_until).slice(0, 10) : '',
        qr_daily_cap: (row && row.qr_daily_cap !== null && row.qr_daily_cap !== undefined) ? Number(row.qr_daily_cap) : 20,
        // ⚠ Defaults ON for a NEW profile. `!!undefined` is false, which would
        // hand every profile created from this screen the least safe setting.
        qr_require_sms_verify: row ? !!row.qr_require_sms_verify : true,
    };
}

/** "7 of 20 used today", or the uncapped form. Read-only. */
export function usageText(issuedToday, cap) {
    const n = Number(issuedToday || 0);
    if (cap === null || cap === undefined || cap === '') return n + ' issued today';

    return n + ' of ' + Number(cap) + ' used today';
}
